/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-carbon',
	'prop-types',
	'../../../../app/ui/dialogs/EditableDialog',
	'../../../../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'../nls/StringResources'
], function(React, UI_TOOLKIT, UI_CARBON, PropTypes, EditableDialog, KeyCodes, StringResources) {
	/**
	 * @description This class provides all of the contents inside a serviceability panel.
	 * The UI is a two-level hierarchy. The first level is Section, and the second level is Field.
	 *
	 * For each Section, it has an attribute called "open" to control if this section should be
	 * opened in default. It also has an attribute called "sectionName" which should be a string.
	 * If "sectionName" is an empty string, the section name component will not be rendered.
	 *
	 * For each Field, it has attributes which are "fieldName", "fieldValue", and "type". They are all
	 * strings. The values of "fieldName" and "fieldValue" could be any string. If "fieldName" is an
	 * empty string, the field name component will not be rendered. "type" is also a string and its
	 * value should be one of ['SimpleTable', 'Default', 'ReactNode']. If "type" is not specified, we will render
	 * it in a default way.
	 *
	 * If the "type" is 'SimpleTable', the "fieldValues" should be an object which has the following properties:
	 * - columnLength: how many columns you want to display
	 * - rowLength: how many rows you want to display
	 * - headerValue: (col) => {return value;}, where "col" is an integer represents the column number, and "value"
	 *                is a string.
	 * - cellValue: (row, col) => {return value;}, where "row" and "col" are row number and col number which both
	 *               are integers. The "value" should be a string.
	 *
	 * A object should be provided to tell this class how to render Sections and Fields. It should looks like:
	 * [
	 *   {
	 *     sectionName: 'section A',
	 *     sectionValues: [{
	 *         fieldName: 'field a1',
	 *         fieldValue: 'field value of a1',
	 * 	       type: 'SimpleTable'
	 *       },
	 *		 {
	 *		   fieldName: 'field a2',
	 *		   fieldValue: 'field value of a2'
	 *		 }
	 *	   ],
	 *	   properties: {
	 *	     open: true | false
	 *	   }
	 *	 },
	 *	 {
	 *	   sectionName: 'section B',
	 *	   sectionValues: [],
	 *	   properties: {}
	 *	 }
	 * ]
	 * Content has all components on a serviceability panel.
	 * It consists of many sections, which consists of many fields.
	 */
	const Label = UI_TOOLKIT.Label;
	const Link = UI_TOOLKIT.Link;
	const Container = UI_TOOLKIT.Container;
	const Button = UI_TOOLKIT.Button;
	const FlexLayout = UI_TOOLKIT.FlexLayout;
	const FlexItem = UI_TOOLKIT.FlexItem;
	const ToggleSwitchSection = UI_TOOLKIT.ToggleSwitchSection;
	const Paragraph = UI_TOOLKIT.Paragraph;
	const Separator = UI_TOOLKIT.Separator;
	const SimpleTable = UI_TOOLKIT.SimpleTable;
	const Accordion = UI_CARBON.Accordion;
	const AccordionItem = UI_CARBON.AccordionItem;
	const FIELD_TITLE_CLASS = 's12yFieldTitle';
	const FIELD_VALUE_CLASS = 's12yFieldValue';
	class Content extends React.Component{
		static propTypes = {
			widgetInfo: PropTypes.shape({
				getSpec: PropTypes.func
			}),
			dashboardApi: PropTypes.any,
			UISpec: PropTypes.array,
			showButtons: PropTypes.bool
		}

		constructor(props){
			super(props);
			this._dashboardS12yFeature = this.props.dashboardApi.getFeature('Serviceability');
			this.state = {
				infoIsVisible: this._dashboardS12yFeature.isInfoVisible()
			};
			this._onClickSpec = this._onClickSpec.bind(this);
			this._onClickShowInfo = this._onClickShowInfo.bind(this);
			this._fieldContentGenerators = {
				'SimpleTable': this._createSimpleTable.bind(this),
				'Default': this._createParagraph.bind(this),
				'ReactNode': fieldSpec => (fieldSpec.node),
				'Toggle': this._createToggleSection.bind(this),
				'Link': this._createLinkSection.bind(this),
				'Label': this._createLabel.bind(this)
			};
		}

		_onClickSpec(){
			let allSpec = { 'widget spec': this.props.widgetInfo.getSpec(), 'dashboard spec': this.props.dashboardApi.getSpec() };
			const options = {
				title: StringResources.get('specification'),
				textContent: JSON.stringify(allSpec, null, 4)
			};
			const boardModelDialog = new EditableDialog(options);
			boardModelDialog.render();
		}

		_onClickShowInfo(willShowInfo) {
			if(willShowInfo === true) {
				this._dashboardS12yFeature.showInfo();
			} else {
				this._dashboardS12yFeature.hideInfo();
			}
			this.setState({ infoIsVisible: willShowInfo });
		}

		render(){
			const sections = this.props.UISpec.map((sectionSpec, index) => this._createSection(sectionSpec, index));

			return <Container width={'auto'} className={'s12yContent'}>
				{this._renderHeader()}
				{this._renderContent(sections)}
			</Container>;
		}

		_renderContent(sections){
			return this.props.isExpanded ? sections : <Accordion>
				{sections}
			</Accordion>;
		}

		_renderHeader(){
			return this.props.header && <Container className={'sectionHeaderContainer'}>
				<FlexLayout direction="row" height={56}>
					<FlexItem grow={2}>
						{this._createLabel({ fieldName: 'SectionHeader', fieldValue: StringResources.get('serviceability') }, 'labelSectionHeader')}
					</FlexItem>
					<FlexItem>
						<Button
							className={'dismissButton'}
							icon={this.props.dashboardApi.getFeature('Icons').getIcon('dismiss')}
							iconSize='small'
							variant="icon"
							title="dismiss"
							onClick={() => this.props.closePanel()}
						/>
					</FlexItem>
				</FlexLayout>
			</Container>;
		}

		_createSection(sectionSpec){
			let fields = sectionSpec.sectionValues.map((fieldSpec, fieldIndex) =>{
				let isLastField = (fieldIndex + 1 === sectionSpec.sectionValues.length);
				let noSpacing;
				if(isLastField) {
					noSpacing = true;
				} else {
					noSpacing = fieldIndex < sectionSpec.sectionValues.length-1 ? sectionSpec.sectionValues[fieldIndex+1].type === 'Toggle' : false;
				}
				return this._createField(fieldSpec, isLastField, noSpacing);
			});

			let section;
			if(this.props.isExpanded){
				section = <React.Fragment>
					{fields}
				</React.Fragment>;
			} else {
				// Take section name, convert to lower case, and get rid of any spaces if any
				let id = 'serviceability_section_' + sectionSpec.sectionName.toLowerCase().replace(/\s/g, '');
				section = <AccordionItem
					id={id}
					aria-controls={id}
					title={sectionSpec.sectionName}
					icon='right'
					open={sectionSpec.properties.open}
					className={`s12ySection`}>
					{fields}
				</AccordionItem>;
			}

			return section;
		}

		_createField(fieldSpec, isLastField, noSpacing=false){
			const shouldAddFieldSeparator = isLastField && !this.props.isExpanded;
			const separator = shouldAddFieldSeparator ? <Separator className={'fieldSeparator'}/> : null;
			let fieldContentGenerator = this._fieldContentGenerators[fieldSpec.type];
			if(!fieldContentGenerator) {
				fieldContentGenerator = this._fieldContentGenerators['Default'];
			}
			const fieldContext = this._getFieldContext(fieldSpec);
			const fieldContent = fieldContentGenerator(fieldSpec, noSpacing);
			// Take field name, convert to lower case, and get rid of any spaces if any
			const id = 'serviceability_field_' + fieldSpec.fieldName.toLowerCase().replace(/\s/g, '');
			return <Container
				id={id}
				aria-controls={id}
				className={'s12yField'}
				tabIndex={fieldContext.tabIndex}
				onKeyDown={fieldContext.onKeyDown}
			>
				{fieldContent}
				{separator}
			</Container>;
		}

		_getFieldContext (fieldSpec = {}) {
			const context = {
				tabIndex: -1
			};
			if (fieldSpec.type === 'Link' && fieldSpec.role === 'button') {
				context.tabIndex = 0;
				context.onKeyDown = this._onKeyDown.bind(this);
			}
			return context;
		}

		_onKeyDown (event) {
			if (event.keyCode === KeyCodes.ENTER || event.keyCode === KeyCodes.SPACE) {
				// Prevent browser's default action: space or enter key is not a click
				// otherwise, if preventDefault is not called, the spec first get loaded loaded then get deleted resulting the
				// boardModelDialog containing no spec
				event.preventDefault();
				this._onClickSpec();
			}
		}

		_createLinkSection(fieldSpec, noSpacing) {
			void(noSpacing);
			return !this.props.showButtons ? null : <Link
				onClick={this._onClickSpec}>
				{fieldSpec.fieldValue}
			</Link>;
		}

		_createToggleSection(fieldSpec){
			const fieldContent = fieldSpec.fieldValue;
			return !this.props.showButtons ? null : <ToggleSwitchSection
				label={
					this.state.infoIsVisible ? fieldContent.toggleSectionLabelHide : fieldContent.toggleSectionLabelShow
				}
				rtl={false}
				className={'showToggleSwitch'}
				checked={this.state.infoIsVisible}
				small={true}
				onChange={this._onClickShowInfo}
			/>;
		}

		_createLabel(fieldSpec, className){
			const labelClassName = className ? className : 'labelText';
			return <Label className={labelClassName} label={fieldSpec.fieldValue}/>;
		}

		_createParagraph(fieldSpec, noSpacing=false) {
			const fieldValueClass = noSpacing ? `${FIELD_VALUE_CLASS} noSpacing` : FIELD_VALUE_CLASS;
			return <Container className={'Default'}>
				<Label className={FIELD_TITLE_CLASS} label={fieldSpec.fieldName} type='caption'/>
				<Paragraph className={fieldValueClass}> {fieldSpec.fieldValue} </Paragraph>
			</Container>;
		}

		_createSimpleTable(fieldSpec){
			return <Container className={'SimpleTable'}>
				{fieldSpec.fieldName === '' ? null : <Label className={FIELD_TITLE_CLASS} label={fieldSpec.fieldName} type='caption'/>}
				<SimpleTable
					className={'s12ySimpleTable'}
					colLength={fieldSpec.fieldValue.columnLength}
					rowLength={fieldSpec.fieldValue.rowLength}
					headerRenderer={(col) => {
						let header;
						if(typeof(fieldSpec.fieldValue.headerValue) === 'function'){
							header = <Paragraph className={`${FIELD_VALUE_CLASS} s12ySimpleTableHeader s12ySimpleTableHeader_${col}`}>{fieldSpec.fieldValue.headerValue(col)}</Paragraph>;
						} else {
							header = null;
						}
						return header;
					}}
					cellRenderer={(row, col) => {
						return <Paragraph className={`${FIELD_VALUE_CLASS} s12ySimpleTableCell s12ySimpleTableCell_${row}_${col}`}>{fieldSpec.fieldValue.cellValue(row,col)}</Paragraph>;
					}}/></Container>;
		}
	}

	Content.propTypes = {
		closePanel: PropTypes.func,
		header: PropTypes.bool,
		isExpanded: PropTypes.bool,
		showButtons: PropTypes.bool
	};

	Content.defaultProps = {
		header: true,
		isExpanded: false,
		showButtons: true
	};

	return Content;
});
